
import { Component, Vue, Prop } from 'vue-property-decorator'
import { FormField } from '@/models'
import numeral from '@/plugins/numeral'

interface NumberFieldOptions {
  icon?: string
  norules: boolean
  rules?: Array<(data: string) => void>
  min?: number
  max?: number
  integer?: boolean
  placeholder?: string
  noicon?: boolean
}

@Component({})
export default class NumberField extends Vue {
  /** Current Value */
  @Prop({ type: Number }) value?: number | null
  /** Validation Errors */
  @Prop() errorMessages!: string | string[]
  /** Field Name */
  @Prop({ type: String, required: true }) name!: string
  /** Field Schema */
  @Prop({ type: Object, default: () => ({}) }) schema!: FormField
  /** Disabled state */
  @Prop({ type: Boolean, default: false }) disabled!: boolean
  /** Rules prop */
  @Prop({ type: Array, required: false }) rules!: Record<string, any>[]
  /** Rules disabled */
  @Prop({ type: Boolean, default: false }) norules!: boolean
  /** Icon disabled */
  @Prop({ type: Boolean, default: true }) noicon!: boolean
  /** outlined prop */
  @Prop({ type: Boolean, default: true }) outlined!: boolean
  /** dense prop */
  @Prop({ type: Boolean, default: true }) dense!: boolean

  get getIcon() {
    if (this.noicon || this.fieldOptions.noicon) return ''
    return this.fieldOptions.icon || 'filter_1'
  }

  get displayValue() {
    if (this.value == null) return
    return numeral(this.value).format('0,0.[00]')
  }

  /** Validation Rules */
  get validationRules() {
    if (this.norules || this.fieldOptions.norules) return []

    const rules = this.rules || this.fieldOptions.rules || []
    // Number validation
    rules.push(
      (v?: string) =>
        !v || Number.isFinite(parseFloat(v)) || this.$t('number_field.script.validationRules.numeric')
    )
    // Required validation
    if (!this.schema.optional) {
      rules.push((v?: string | null) => !!v || this.$t('number_field.script.validationRules.required'))
    }
    // Max number validation
    if (this.fieldOptions.max) {
      rules.push(
        (v?: string) =>
          numeral(v).value() <= this.fieldOptions.max! ||
          `${this.$t('number_field.script.validationRules.exced')} ${this.fieldOptions.max}.`
      )
    }
    // Min number validation
    if (this.fieldOptions.min) {
      rules.push(
        (v?: string) =>
          numeral(v).value() >= this.fieldOptions.min! ||
          `${this.$t('number_field.script.validationRules.under')} ${this.fieldOptions.min}.`
      )
    }
    // Integer validation
    if (this.fieldOptions.integer) {
      rules.push(
        (v?: string) => numeral(v).value() % 1 === 0 || this.$t('number_field.script.validationRules.integer')
      )
    }
    return rules
  }

  /** Additional field options */
  get fieldOptions(): NumberFieldOptions {
    return this.schema.options || this.schema.fieldOptions || {}
  }

  /** Input event handler */
  handleInput(value: string) {
    this.$emit('input', value !== '' ? numeral(value).value() : undefined)
  }
}
